import React, { useEffect, useState } from "react";
import { SlidersHorizontal, Loader2, Save, Power } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PROMO_RULE_DEFAULTS } from "@/components/marketplace/Promo";

// Lot 700 — Admin › Promos Marketplace › « Réglages » : interrupteur de la fonctionnalité (features.marketplace_promos)
// + règles appliquées par le serveur à toute promo (remise min / max en %, durée max en jours, promos simultanées par
// vendeur). OFF = plus de « Lancer une promo », plus de « Promos en cours », plus de « Promo du jour », promos existantes
// masquées (pas effacées). Enregistrement réservé au super admin (PATCH /admin/settings) ; les autres voient en lecture seule.
const FIELDS = [
  { key: "min_percent", label: "Remise minimale", unit: "%", min: 1, max: 95, hint: "En dessous, le serveur refuse la promo" },
  { key: "max_percent", label: "Remise maximale", unit: "%", min: 1, max: 95, hint: "Au-dessus, le serveur refuse la promo" },
  { key: "max_days", label: "Durée maximale", unit: "jours", min: 1, max: 90, hint: "Date de fin la plus lointaine autorisée" },
  { key: "max_active_per_seller", label: "Promos simultanées / vendeur", unit: "", min: 1, max: 50, hint: "Promos en cours au même moment pour un même vendeur" },
];

export const PromoRulesCard = ({ canEdit = false }) => {
  const { setConfig } = useApp();
  const [enabled, setEnabled] = useState(true);
  const [rules, setRules] = useState(PROMO_RULE_DEFAULTS);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      const s = data?.settings || data || {};
      setEnabled(s.features?.marketplace_promos !== false);
      setRules({ ...PROMO_RULE_DEFAULTS, ...(s.promo_rules || {}) });
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);
  const save = async (next = { enabled, rules }) => {
    setSaving(true);
    try {
      const clean = Object.fromEntries(FIELDS.map((f) => [f.key, Math.max(f.min, Math.min(f.max, Number(next.rules[f.key]) || PROMO_RULE_DEFAULTS[f.key]))]));
      if (clean.max_percent < clean.min_percent) clean.max_percent = clean.min_percent;
      const { data } = await api.patch("/admin/settings", { features: { marketplace_promos: next.enabled }, promo_rules: clean });
      const s = data?.settings || {};
      setRules({ ...PROMO_RULE_DEFAULTS, ...(s.promo_rules || clean) });
      setEnabled(s.features?.marketplace_promos !== false);
      // La config publique (utilisée par la Marketplace des membres) est mise à jour sur place : effet immédiat côté admin.
      setConfig((c) => ({ ...c, settings: { ...(c?.settings || {}), promo_rules: s.promo_rules || clean, features: { ...((c?.settings || {}).features || {}), marketplace_promos: next.enabled } } }));
      toast.success(next.enabled ? "Réglages des promos enregistrés" : "Promos Marketplace désactivées — plus aucune promo n'est visible");
    } catch (e) { toast.error(e?.response?.data?.detail || e?.friendlyMessage || "Enregistrement impossible"); }
    finally { setSaving(false); }
  };
  const toggle = (v) => { setEnabled(v); save({ enabled: v, rules }); };
  return (
    <div className="yalola-card p-5 space-y-3" data-testid="promo-rules-card" data-enabled={enabled ? "1" : "0"}>
      <div className="flex items-center gap-2 flex-wrap">
        <SlidersHorizontal size={18} style={{ color: "var(--yalola-primary)" }} />
        <div className="font-bold font-display text-lg">Réglages des promos</div>
        {!canEdit && <span className="chip text-[11px]" style={{ background: "var(--yalola-surface-alt)" }}>Lecture seule — super admin requis</span>}
        <label className="ml-auto inline-flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="promo-rules-enabled-label">
          <Power size={14} style={{ color: enabled ? "#1B7A3D" : "#B42318" }} />
          <span style={{ color: enabled ? "#1B7A3D" : "#B42318" }}>{enabled ? "Promos activées" : "Promos désactivées"}</span>
          <Switch checked={enabled} onCheckedChange={toggle} disabled={!canEdit || saving || !loaded} data-testid="promo-rules-enabled" />
        </label>
      </div>
      <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
        {enabled
          ? "Les vendeurs peuvent lancer une promo depuis leur fiche ou à la publication (« Promo de lancement »). Le serveur refuse toute promo hors de ces règles ; le pourcentage est calculé à partir du prix de référence et du prix promo."
          : "DÉSACTIVÉ : plus de « Lancer une promo », plus de section « Promos en cours » ni de « Promo du jour » ; les promos déjà posées sont masquées (prix de référence seul) sans être effacées — elles réapparaissent si tu réactives."}
      </p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <div className="text-xs font-semibold">{f.label}{f.unit ? ` (${f.unit})` : ""}</div>
            <Input type="number" min={f.min} max={f.max} value={rules[f.key] ?? ""} disabled={!canEdit || !enabled} onChange={(e) => setRules((r) => ({ ...r, [f.key]: e.target.value }))} className="mt-1 h-9" data-testid={`promo-rule-${f.key}`} />
            <div className="text-[11px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{f.hint}</div>
          </div>
        ))}
      </div>
      {canEdit && (
        <div className="flex justify-end">
          <Button size="sm" className="pill-primary rounded-full" disabled={saving || !loaded || !enabled} onClick={() => save()} data-testid="promo-rules-save">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} <span className="ml-1">Enregistrer les règles</span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default PromoRulesCard;
